import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useRecoilState } from 'recoil';
import { yearState } from '../recoil';
import { getActivityList } from '../services';
import { Activity } from '../types';
import { currentYear, getRangeDateParams } from '../utils/date';
import { loadState, saveState } from '../utils/localStorage';
import { useGetAccessToken } from './useGetAccessToken';

const staleTime = 10 * 60 * 1000; // 10 minutes

// Promise.all / Promise.allSettled -> en série
// useQueries -> en parallèle, de manière asynchrone, les queries arrivent petit à petit, pas bon pour l'UX
/**
 *
 * @param access_token
 * @param year
 * @returns
 * @todo: loop through activity pages received
 */
export const getActivities = (access_token: string, year: number) => {
  const { after, before } = getRangeDateParams(Number(year));
  /** @todo loop through all pages */
  const nbPages = 5;
  return Promise.allSettled(
    [...Array(nbPages).keys()].map((page) =>
      getActivityList(access_token, after, before, page)
    )
  ).then((results) =>
    results
      .map((result) => (result.status === 'fulfilled' ? result.value : []))
      .flat()
      .filter((activity) => activity.hasOwnProperty('id'))
  );
};

export const useActivities = (YEAR?: number) => {
  const [recoilYear] = useRecoilState(yearState);
  const year = YEAR || recoilYear || currentYear;
  const KEY = 'activities_' + year;

  const { access_token } = useGetAccessToken();

  // data stored in local storage from previous session
  const initialData: Activity[] = loadState(KEY);

  const {
    data: activities,
    dataUpdatedAt,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: [`activities ${year}`],
    queryFn: () => getActivities(access_token || '', year),
    staleTime,
    enabled: access_token !== undefined,
    initialData,
  });

  useEffect(() => {
    if (!error && !isLoading) {
      saveState(KEY, activities);
    }
  }, [activities, error, isLoading]);

  return {
    isLoading,
    isFetching,
    error,
    activities,
    refetch,
    dataUpdatedAt,
  };
};
